// src/pages/FavoritesPage.jsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  getUserFavoritesWithData, 
  removePlaceAsFavorite, 
  fetchDistinctMetros,
  translateFavorites
} from '../services/api';
import HeartIcon from '../components/icons/HeartIcon';
import './FavoritesPage.css';

// Global variables that persist between renders
let savedScrollPosition = 0;
let savedMetro = '';

const FavoritesPage = () => {
  const location = useLocation();
  const { user, getToken } = useAuth();
  const userId = user?.id;
  
  // Extract state from router if it exists
  const routerState = location.state || {};
  
  const [favorites, setFavorites] = useState([]);
  const [metros, setMetros] = useState([]);
  // Initialize with saved metro from router state or previously saved metro
  const [selectedMetro, setSelectedMetro] = useState(routerState.metro || savedMetro || '');
  const [translationResults, setTranslationResults] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isTranslating, setIsTranslating] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [expandedFavorites, setExpandedFavorites] = useState(false);
  const initialVisibleCount = 3;
  
  // Cache for translation results
  const translationCache = {};
  
  // Load favorites
  useEffect(() => {
    if (!userId) {
      setIsLoading(false);
      return;
    }
    
    const fetchFavorites = async () => {
      try {
        const token = getToken();
        const data = await getUserFavoritesWithData(token);
        setFavorites(data || []);
      } catch (err) {
        console.error('[FavoritesPage] Error fetching favorites:', err);
        setErrorMessage('Failed to load favorites');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchFavorites();
  }, [userId, getToken]);

  // Load metros
  useEffect(() => {
    const loadMetros = async () => {
      try {
        const data = await fetchDistinctMetros();
        setMetros(data);
      } catch (err) {
        console.error('[FavoritesPage] Error fetching metros:', err);
      }
    };
    
    loadMetros();
  }, []);

  // Handle translation when metro changes
  useEffect(() => {
    if (!userId || !selectedMetro) {
      setTranslationResults([]);
      return;
    }
    
    // Save selected metro in global variable
    savedMetro = selectedMetro;
    
    // Use cached results if available
    if (translationCache[selectedMetro]) {
      console.log(`[FavoritesPage] Using cached results for ${selectedMetro}`);
      setTranslationResults(translationCache[selectedMetro]);
      
      // Restore scroll position after a short delay to ensure rendering is complete
      setTimeout(() => {
        const scrollY = routerState.scrollY || savedScrollPosition || 0;
        console.log(`[FavoritesPage] Restoring scroll position to ${scrollY}`);
        window.scrollTo(0, scrollY);
      }, 300);
      
      return;
    }

    const runTranslation = async () => {
      setIsTranslating(true);
      try {
        const token = getToken();
        const data = await translateFavorites(token, userId, selectedMetro);
        
        // Cache the results
        translationCache[selectedMetro] = data;
        
        setTranslationResults(data);
        
        // Restore scroll position after a short delay
        setTimeout(() => {
          const scrollY = routerState.scrollY || savedScrollPosition || 0;
          console.log(`[FavoritesPage] Restoring scroll position to ${scrollY}`);
          window.scrollTo(0, scrollY);
        }, 300);
      } catch (err) {
        console.error('[FavoritesPage] Error in translation:', err);
        setErrorMessage('Translation failed. Please try again.');
      } finally {
        setIsTranslating(false);
      }
    };

    runTranslation();
  }, [userId, selectedMetro, getToken, routerState.scrollY]);
  
  // Save scroll position every time it changes
  useEffect(() => {
    const handleScroll = () => {
      savedScrollPosition = window.scrollY;
      console.log(`[FavoritesPage] Saved scroll position: ${savedScrollPosition}`);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle unfavorite
  const handleUnfavorite = async (placeInternalId) => {
    try {
      const token = getToken();
      await removePlaceAsFavorite(token, placeInternalId);
      setFavorites((prev) => prev.filter((f) => f.place_internal_id !== placeInternalId));
      
      // Also remove from translation results if active
      if (selectedMetro) {
        // First update local state
        const newResults = translationResults.filter(
          group => group.savedPlaceId !== placeInternalId
        );
        setTranslationResults(newResults);
        
        // Then update the cache
        if (translationCache[selectedMetro]) {
          translationCache[selectedMetro] = translationCache[selectedMetro].filter(
            group => group.savedPlaceId !== placeInternalId
          );
        }
      }
    } catch (err) {
      console.error('Error removing favorite:', err);
      setErrorMessage('Failed to remove favorite');
    }
  };

  // Create navigation state to pass with Links
  const createNavigationState = () => {
    return {
      metro: selectedMetro,
      scrollY: window.scrollY
    };
  };

  if (!userId) {
    return (
      <div className="favorites-page">
        <div className="container">
          <h2>My Favorites</h2>
          <p>Please sign in to view your favorites.</p>
        </div>
      </div>
    );
  }

  const visibleFavorites = expandedFavorites 
    ? favorites 
    : favorites.slice(0, initialVisibleCount);

  return (
    <div className="favorites-page">
      <div className="container">
        <h2>my favorites</h2>
        
        {isLoading ? (
          <div className="loading-indicator">Loading favorites...</div>
        ) : favorites.length === 0 ? (
          <p>You don't have any favorites yet.</p>
        ) : (
          <div className="favorites-list">
            {visibleFavorites.map((fav) => (
              <div key={fav.saved_id} className="favorite-item">
                <div className="favorite-content">
                  {fav.slug ? (
                    <Link 
                      to={`/p/${fav.slug}`} 
                      state={{ returnToFavorites: createNavigationState() }}
                      className="favorite-link"
                    >
                      <span className="favorite-name">{fav.name}</span>
                      {fav.city && <span className="favorite-city">({fav.city})</span>}
                    </Link>
                  ) : (
                    <>
                      <span className="favorite-name">{fav.name}</span>
                      {fav.city && <span className="favorite-city">({fav.city})</span>}
                    </>
                  )}
                  <button
                    className="unfavorite-button"
                    onClick={() => handleUnfavorite(fav.place_internal_id)}
                  >
                    <HeartIcon isSelected />
                  </button>
                </div>
              </div>
            ))}
            
            {favorites.length > initialVisibleCount && (
              <button 
                className="expand-button"
                onClick={() => setExpandedFavorites(!expandedFavorites)}
              >
                {expandedFavorites 
                  ? 'Show Less' 
                  : `Show More (${favorites.length - initialVisibleCount} more)`}
              </button>
            )}
          </div>
        )}

        <div className="divider"></div>

        <h2>find your favorites anywhere</h2>
        <p className="section-desc">
          choose a city below to discover spots just like your favorites — wherever you go.
        </p>

        <div className="metro-selector">
          <label htmlFor="metroSelect">choose a city:</label>
          <select
            id="metroSelect"
            value={selectedMetro}
            onChange={(e) => setSelectedMetro(e.target.value)}
          >
            <option value="">-- city --</option>
            {metros.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>

        {selectedMetro && (
          <div className="translation-results">
            {isTranslating ? (
              <div className="loading-indicator">Finding matches...</div>
            ) : translationResults.length === 0 ? (
              <p>No results found for that city.</p>
            ) : (
              <>
                <h3>places in {selectedMetro} you'll love</h3>
                {translationResults.map((group) => (
                  <div key={group.savedPlaceId} className="translation-group">
                    <p className="translation-favorite">
                      <strong>Favorite:</strong> {group.savedPlaceName}
                      {group.savedPlaceCity ? ` (${group.savedPlaceCity})` : ''}
                    </p>
                    {group.translatedPlaces && group.translatedPlaces.length > 0 ? (
                      <ul className="translated-places">
                        {group.translatedPlaces.map((tp) => (
                          <li key={tp.id}>
                            {tp.slug ? (
                              <Link 
                                to={`/p/${tp.slug}`}
                                state={{ returnToFavorites: createNavigationState() }}
                              >
                                {tp.name}
                              </Link>
                            ) : (
                              <span>{tp.name}</span>
                            )}
                            {tp.metro_area ? ` [${tp.metro_area}]` : ''}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p>No matching places found in {selectedMetro} for this favorite.</p>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {errorMessage && <p className="error-message">{errorMessage}</p>}
      </div>
    </div>
  );
};

export default FavoritesPage;